import React, {useEffect, useState} from "react";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography} from "@mui/material";
import {getBackendErrorMessage} from "../../../shared/utils/httpError";
import {createPlano, listPlanos} from "../PlanosComerciaisService";
import {MODALIDADES} from "../planoVersaoUtils";

const vazio = (sistemaId = '') => ({id_sistema: sistemaId, nome_plano: '', descricao: '', modalidade: 'PRE_PAGO'});

/*
 * Plano novo: nasce com a versão 1 em rascunho, sem módulos. A pessoa monta a versão e publica na página do plano.
 *
 * O POST do app-funcionalidades publica a versão 1 na hora quando o plano é ATIVO. Por isso o plano nasce INATIVO,
 * fora da vitrine, e a página do plano o ativa ao publicar a primeira versão. O POST não devolve o id: a tela
 * procura o plano recém-criado pelo nome no sistema.
 */
export default function NovoPlanoDialog({aberto, sistemas = [], sistemaInicial = '', onFechar, onCriado}) {
    const [form, setForm] = useState(vazio(sistemaInicial));
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (aberto) {
            setForm(vazio(sistemaInicial));
            setErro('');
        }
    }, [aberto, sistemaInicial]);

    const mudar = (campo) => (event) => setForm((atual) => ({...atual, [campo]: event.target.value}));

    const criar = async () => {
        const nome = form.nome_plano.trim();
        if (!form.id_sistema || !nome) {
            setErro('Escolha o sistema e dê um nome ao plano.');
            return;
        }
        setSalvando(true);
        setErro('');
        try {
            await createPlano({
                id_sistema: form.id_sistema,
                nome_plano: nome,
                descricao: form.descricao.trim(),
                tipo_cobranca: form.modalidade,
                preco_base_mensal: 0,
                permite_addons: false,
                possui_suporte: true,
                status: 'INATIVO',
                vitrine: false,
                modulos: [],
            });
            const candidatos = (await listPlanos({id_sistema: form.id_sistema, q: nome}))
                .filter((plano) => plano.nome_plano === nome)
                .sort((a, b) => String(b.criado_em || '').localeCompare(String(a.criado_em || '')));
            onCriado(candidatos[0] || null);
        } catch (error) {
            setErro(getBackendErrorMessage(error, 'Não foi possível criar o plano.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={aberto} onClose={salvando ? undefined : onFechar} fullWidth maxWidth="sm">
            <DialogTitle>Novo plano</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Typography variant="body2" color="text.secondary">
                        O plano nasce com a versão 1 em rascunho. Nela você escolhe os meios de pagamento, o trial, o preço e os módulos, e publica quando estiver pronta.
                    </Typography>
                    {erro ? <Alert severity="error">{erro}</Alert> : null}
                    <TextField select label="Sistema" value={form.id_sistema} onChange={mudar('id_sistema')} required fullWidth>
                        {sistemas.map((sistema) => <MenuItem key={sistema.id} value={sistema.id}>{sistema.nome}</MenuItem>)}
                    </TextField>
                    <TextField label="Nome do plano" value={form.nome_plano} onChange={mudar('nome_plano')} required fullWidth inputProps={{maxLength: 100}} />
                    <TextField label="Descrição" value={form.descricao} onChange={mudar('descricao')} fullWidth multiline minRows={2} />
                    <TextField select label="Modalidade" value={form.modalidade} onChange={mudar('modalidade')} fullWidth
                               helperText={MODALIDADES.find((item) => item.value === form.modalidade)?.texto}>
                        {MODALIDADES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                    </TextField>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onFechar} disabled={salvando}>Cancelar</Button>
                <Button variant="contained" onClick={criar} disabled={salvando}>{salvando ? 'Criando…' : 'Criar plano'}</Button>
            </DialogActions>
        </Dialog>
    );
}
